import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

export type QueryOptions<Response, Key extends QueryKey> = Omit<
  UseQueryOptions<Response, Error, Response, Key>,
  'queryKey' | 'queryFn'
>;

export type MutationOptions<TData, TVariables> = Omit<
  UseMutationOptions<TData, Error, TVariables, unknown>,
  'mutationFn'
>;

// Types for the configuration
export type QueryHookFactory = <Response, Key extends QueryKey>(queryConfig: {
  queryKey: Key;
  queryFn: QueryFunction<Response, Key>;
}) => (
  options?: Omit<
    UseQueryOptions<Response, Error, Response, Key>,
    'queryKey' | 'queryFn'
  >
) => UseQueryResult<Response, Error>;

export type MutationHookFactory = <TData, TVariables>(
  mutationFn: MutationFunction<TData, TVariables>
) => (
  options?: Omit<
    UseMutationOptions<TData, Error, TVariables, unknown>,
    'mutationFn'
  >,
  authElevationOptions?: any // accepted but ignored
) => any;

// Implementations

export const createQueryHook: QueryHookFactory = (queryConfig) => {
  return (options) =>
    useQuery({
      ...queryConfig,
      ...options,
    });
};

export const createMutationHook: MutationHookFactory = (mutationFn) => {
  return (options, _authElevationOptions) =>
    useMutation({
      mutationFn,
      ...options,
    });
};
